import { FILTER_URL_KEYS } from '@/src/lib/filters/filterTypes';
import { buildSrpPagePath, type QueryValue } from '@/src/lib/seo/srp-pagination';
import { SRP_MAP_URL_KEYS, SRP_VIEWPORT_URL_KEYS } from '@/src/lib/seo/paramCrawlPolicy';

type SearchParamsLike = {
  forEach(callbackfn: (value: string, key: string) => void): void;
  toString(): string;
};

type QuerySource = SearchParamsLike | Record<string, QueryValue> | null | undefined;

const SRP_PAGE_QUERY_KEY = 'page';
const FILTER_QUERY_KEYS = new Set<string>(FILTER_URL_KEYS);
// Built from SRP_MAP_URL_KEYS so the crawl policy and the carryover set cannot disagree
// about which map params exist. paramCrawlPolicy holds them as a literal tuple because it
// needs them at type level; this module only ever needs membership.
const SRP_VIEWPORT_QUERY_KEYS = new Set<string>(SRP_VIEWPORT_URL_KEYS);
const SRP_MAP_QUERY_KEYS = new Set<string>(SRP_MAP_URL_KEYS);
const CARRYOVER_QUERY_KEYS = new Set<string>([
  ...FILTER_URL_KEYS,
  ...SRP_MAP_QUERY_KEYS,
]);

function isSearchParamsLike(source: QuerySource): source is SearchParamsLike {
  return typeof source === 'object' && source !== null && typeof (source as SearchParamsLike).forEach === 'function';
}

function toSearchParams(source: QuerySource): URLSearchParams {
  const params = new URLSearchParams();
  if (!source) return params;

  if (isSearchParamsLike(source)) {
    source.forEach((value, key) => {
      params.append(key, value);
    });
    return params;
  }

  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
      return;
    }

    params.append(key, value);
  });

  return params;
}

function appendSearch(pathname: string, params: URLSearchParams): string {
  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

export function buildSrpPaginationHref(
  pathname: string,
  page: number,
  searchParams: QuerySource,
  fallbackQuery?: QuerySource,
): string {
  const currentParams = toSearchParams(searchParams);
  const sourceParams = currentParams.toString() ? currentParams : toSearchParams(fallbackQuery);
  const nextParams = new URLSearchParams();

  sourceParams.forEach((value, key) => {
    if (key !== SRP_PAGE_QUERY_KEY) {
      nextParams.append(key, value);
    }
  });

  return appendSearch(buildSrpPagePath(pathname, page), nextParams);
}

export function buildSrpFilterCarryoverHref(targetHref: string, searchParams: QuerySource): string {
  const url = new URL(targetHref, 'https://www.hotpads.com');
  const currentParams = toSearchParams(searchParams);
  const hasCompleteViewport = Array.from(SRP_VIEWPORT_QUERY_KEYS).every((key) => currentParams.has(key));

  CARRYOVER_QUERY_KEYS.forEach((key) => {
    url.searchParams.delete(key);
  });

  currentParams.forEach((value, key) => {
    if (FILTER_QUERY_KEYS.has(key) || (hasCompleteViewport && SRP_MAP_QUERY_KEYS.has(key))) {
      url.searchParams.append(key, value);
    }
  });

  return `${url.pathname}${url.search}${url.hash}`;
}
